import React, { useEffect, useState, useCallback } from 'react'

// Material-UI
import { Button, Container, Grid } from '@mui/material'
import { FaRegCheckCircle } from 'react-icons/fa'
import './Alerts.scss'
import { IARDialog } from '../../../Shared/Dialog/IARDialog'
import { useErrorSnackBarContext } from '../../../Shared/ErrorSnackBar/ErrorSnackBarContext'
import Interceptor from '../../../../Services/serviceBase.interceptor'
import { useMenuSectionContext } from './../../MenuSection/MenuSectionContext'
import { useCMSContext } from '../../CMSContext'
import ButtonSave from '../../../Shared/ButtonSave/ButtonSave'
import { useGetIsDemoDepartment } from '../../../../../resources/useGetIsDemoDepartment'
import CustomDropdown from '../../../Shared/Dropdown/dropdown'
import CommunityTextField from '../../../Shared/CommunityTextField'
import ChooseImage from '../../../Shared/ChooseImage'
import MediaThumbnail from '../../../Shared/MediaThumbnail/MediaThumbnail'
import { IARRte } from '../../../Shared/iar-rte'
import MediaFilesMessage from '../../../Shared/MediaFilesMessage'
import {
  handleChangeForAlertState,
  INITIAL_STATE,
  removeAsset,
  fieldValidator,
  getImagesPayload,
  postAlertRequest
} from './AlertsFunctions'

function Alerts({ fireDepartment }) {
  Interceptor.interceptor(useErrorSnackBarContext().errorSnackBarActions)
  const { errorSnackBarActions } = useErrorSnackBarContext()
  const menuSectionContext = useMenuSectionContext()
  const { setLastCustomSectionId } = menuSectionContext.actions
  const cmsContext = useCMSContext()
  const { iarMember, isReadOnlyMember, isDemoDepartments } = cmsContext.state
  const [dialog, setDialog] = useState({ open: false })
  const { isDemo } = useGetIsDemoDepartment(isDemoDepartments, fireDepartment)
  const [alertState, setAlertState] = useState(INITIAL_STATE)
  const [bodyText, setBodyText] = useState('')
  const [isSending, setIsSending] = useState(false)

  useEffect(() => {
    setLastCustomSectionId('')
  }, [])

  useEffect(() => {
    setBodyText(alertState.bodyText || '')
  }, [alertState.bodyText])

  const renderSavedMessage = () => {
    if (alertState.posted) {
      return (
        <div className="save-message">
          <FaRegCheckCircle className="check-circle" />
          <span className="save-message-text">{'sent!'}</span>
        </div>
      )
    }
  }

  const discardChanges = () => {
    setDialog({ open: false })
    setAlertState({ ...INITIAL_STATE, isDiscarded: true })
    setTimeout(() => {
      setAlertState((prevState) => ({
        ...prevState,
        isDiscarded: false
      }))
    }, 100)
  }

  const openConfirmationDialog = () => {
    setDialog({ open: true })
  }

  const cancelDialog = () => {
    setDialog({ open: false })
  }

  const setImages = useCallback((images = []) => {
    setAlertState((state) => ({
      ...state,
      images
    }))
  }, [])

  const sendAlertInfo = async () => {
    if (isSending) return

    const { ready } = fieldValidator(alertState.title, bodyText, setAlertState)
    if (!ready) return

    const { imgPayload } = getImagesPayload(alertState.images)
    const data = {
      fireDepartmentIdentifier: fireDepartment.identifier,
      title: alertState.title,
      bodyText,
      agencyName: fireDepartment.fireDepartmentName,
      images: imgPayload,
      videos: [],
      onlineVideos: [],
      positionFiles: alertState.positionFiles,
      date: new Date(),
      memberId: iarMember.memberId,
      userName: iarMember.fullName,
      localDate: new Date()
    }

    const formData = new FormData()

    formData.append('request', JSON.stringify(data))
    alertState.images.forEach((image) => {
      if (!(image.file instanceof Blob)) return
      formData.append('images', image.file, image.originalName)
    })

    setIsSending(true)
    try {
      await postAlertRequest(formData, iarMember, setAlertState, errorSnackBarActions)
    } finally {
      setIsSending(false)
    }
  }
  const rteChangeHandler = (evt) => {
    setBodyText(evt)
  }

  const renderRichTextControl = useCallback(() => {
    if (alertState && alertState.isDiscarded) {
      return (
        <IARRte
          onChange={rteChangeHandler}
          content={alertState.bodyText}
          discarded={alertState.isDiscarded}
        />
      )
    } else {
      return (
        <IARRte
          onChange={rteChangeHandler}
          content={alertState.bodyText}
          discarded={alertState.isDiscarded}
        />
      )
    }
  }, [alertState.isDiscarded, rteChangeHandler])

  const updateAsset = useCallback(
    (file) => {
      if (alertState.filesToUpdate.filter((el) => el.fileName === file.fileName).length > 0) {
        alertState.filesToUpdate.filter((el) => el.fileName === file.fileName)[0].caption =
          file.caption
      } else {
        setAlertState((prevState) => ({
          ...prevState,
          filesToUpdate: [
            ...prevState.filesToUpdate,
            { caption: file.caption, fileName: file.fileName }
          ]
        }))
      }
    },
    [alertState.filesToUpdate]
  )

  const handleDropdownChange = useCallback((evt) => {
    const value = evt.target.value
    setAlertState((prevState) => ({ ...prevState, positionFiles: parseInt(value) }))
  }, [])

  return (
    <div className="App alerts">
      <div className="page-header" id="alertTitle">
        Alerts
      </div>
      <IARDialog
        open={dialog.open}
        title="Confirm"
        content="Do you want to discard your changes?"
        onOk={discardChanges}
        onCancel={cancelDialog}
      />

      <Container maxWidth="lg">
        <Grid container spacing={2}>
          <Grid size={12}></Grid>
          <Grid size={12}>
            <div className="page-description" id="alerts-text-desc">
              From this page, you can send customized alerts to the members of your community that
              use the MyLS app, including things such as road closures, impending natural disaster
              warnings; evacuation notices; and any other important alerts. Please limit use of this
              to critical matters so that members of the community do not disable their notification
              settings due to over-use of this.
            </div>
          </Grid>
          <Grid size={12}>
            <form noValidate autoComplete="off">
              <Grid container id="title-field-container">
                <Grid size={12} className="container-field">
                  <label htmlFor="Tilte">Title</label> <br />
                  <label htmlFor="error" className="error-text">
                    {alertState.errors.titleError}
                  </label>
                  <CommunityTextField
                    className="form-control"
                    id="title-field"
                    name="title"
                    variant="outlined"
                    value={alertState.title}
                    onChange={(e) => handleChangeForAlertState(e, setAlertState)}
                  />
                </Grid>
              </Grid>
              <Grid size={12} className="container-field body-text" id="rich-container-text">
                <label htmlFor="message">Body text</label> <br />
                <label htmlFor="error" className="error-text">
                  {alertState.errors.bodyTextError}
                </label>
                {renderRichTextControl()}
              </Grid>
              <Grid size={12} className="container-field dropdown-container">
                <div>
                  <label htmlFor="positionFiles">Position images</label> <br />
                  {alertState.positionFiles ? (
                    <CustomDropdown
                      name="positionFiles"
                      value={1}
                      onChange={(e) => handleDropdownChange(e)}
                    />
                  ) : (
                    <CustomDropdown
                      name="positionFiles"
                      value={0}
                      onChange={(e) => handleDropdownChange(e)}
                    />
                  )}
                </div>
              </Grid>
              <Grid container>
                <Grid size={12}>
                  <Grid className="container-field" id="media-message-container">
                    <label htmlFor="images" className="body-text">
                      Media Files
                    </label>
                    <MediaFilesMessage max={1} alerts={true}></MediaFilesMessage>
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={6} className="container-field media-files" id="container-images-media">
                {fireDepartment && (
                  <ChooseImage
                    images={alertState.images}
                    setImages={setImages}
                    fireDepartmentIdentifier={fireDepartment.identifier}
                    max={1}
                    elementsAdded={alertState.images.length}
                    setErrorLabel={() => {}}
                    isMessageEnabled={false}
                  ></ChooseImage>
                )}
                <>
                  {alertState.images.map((el, index) => {
                    return (
                      <MediaThumbnail
                        type="image"
                        key={el.fileName ? el.fileName : index}
                        media={el}
                        removeMediaEvent={() => removeAsset(el, alertState.images, setAlertState)}
                        onUpdate={() => updateAsset(el)}
                      />
                    )
                  })}
                </>
              </Grid>
              <Grid size={12} className="container container-buttons" id="btn-father">
                {!isDemo && (
                  <div className="save-button-container1">
                    <ButtonSave
                      id="saveButton"
                      disabled={isReadOnlyMember || isSending}
                      loading={isSending}
                      onClickCallback={sendAlertInfo}
                      text="Send"
                    />
                    <Button
                      disabled={isReadOnlyMember}
                      variant="outlined"
                      id="cancelButton2"
                      color="primary"
                      size="medium"
                      onClick={openConfirmationDialog}
                    >
                      Discard
                    </Button>
                    {renderSavedMessage()}
                  </div>
                )}
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Container>
    </div>
  )
}

export default Alerts
